import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Seo from "../components/Seo";
import articleHtml from "../content/insights/what-is-ai-agent.html?raw";

const META = {
  title: "What Is an AI Agent? | Zero Harm AI",
  description:
    "A technical primer for engineers building AI agent systems — covering agent architecture, memory, planning, MCP, A2A, and production failure modes.",
  url: "https://www.zeroharmai.com/insights/what-is-an-ai-agent",
  datePublished: "2026-07-08",
};

export default function WhatIsAIAgent() {
  return (
    <>
      <Seo
        title={META.title}
        description={META.description}
        path="/insights/what-is-an-ai-agent"
        type="article"
      />
      <Helmet>
        <meta property="article:published_time" content={META.datePublished} />
        <meta property="article:author" content="Dzung Tran" />
        <meta property="article:section" content="AI Engineering" />

        {/* Article schema markup */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "TechArticle",
          "headline": "What Is an AI Agent?",
          "description": META.description,
          "url": META.url,
          "datePublished": META.datePublished,
          "dateModified": META.datePublished,
          "image": "https://www.zeroharmai.com/insights/zero-harm-ai.png",
          "author": {
            "@type": "Person",
            "name": "Dzung Tran",
            "url": "https://www.zeroharmai.com/about"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Zero Harm AI",
            "url": "https://www.zeroharmai.com",
            "logo": {
              "@type": "ImageObject",
              "url": "https://www.zeroharmai.com/zero-harm-ai-logo.png"
            }
          },
          "articleSection": "AI Engineering",
          "keywords": [
            "AI agents", "MCP", "A2A", "agent architecture",
            "multi-agent systems", "Model Context Protocol",
            "Agent-to-Agent Protocol", "LLM", "RAG"
          ]
        })}</script>
      </Helmet>

      <article className="max-w-4xl mx-auto px-4 py-10">
        <Link
          to="/insights"
          className="inline-block text-sm font-semibold text-blue-700 hover:text-blue-800 mb-8"
        >
          ← Back to Insights
        </Link>

        <header className="mb-8 border-b border-gray-200 pb-8">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-700 mb-3">
            AI Engineering
          </p>
          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
            What Is an AI Agent?
          </h1>
          <p className="text-gray-600">
            A technical primer for engineers building AI agent systems.
          </p>
          <p className="text-sm text-gray-500 mt-4">
            By Dzung Tran &nbsp;·&nbsp; Published July 8, 2026
          </p>
        </header>

        <div
          className="insight-article"
          dangerouslySetInnerHTML={{ __html: articleHtml }}
        />

        <section className="mt-12 bg-blue-50 border border-blue-100 rounded-xl p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Building or securing AI agents?
          </h2>
          <p className="text-gray-700 mb-5">
            Zero Harm AI designs and builds production AI agents, and the AI security that
            keeps them safe.
          </p>
          <Link
            to="/contact"
            className="inline-block px-5 py-2.5 bg-blue-700 text-white rounded-lg hover:bg-blue-800 font-semibold"
          >
            Talk to Zero Harm AI
          </Link>
        </section>
      </article>
    </>
  );
}
